import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Download, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Per-table metadata ────────────────────────────────────────────────────

const TABLE_META = {
  'domain-config': {
    title:       'domain_configuration',
    heading:     'ESI/MPL Partners',
    subtitle:    '1 row per partner company. Anchor table for all partner joins.',
    breadcrumb:  'ESI/MPL Partners',
    // Explicit column order: key info first, seat limits last
    columnOrder: [
      'id',
      'partner_one_id',
      'esi_partner_name',
      'type',
      'partner_group',
      'microsoft_account_manager',
      'pdm_reporting_manager',
      'partner_org_rm_user_id',
      'is_jump_start',
      'is_block',
      'created_at',
      'updated_at',
      'managed_by',
      'distributable_seats',
      // seat limits pushed to the end
      'allowed_seats',
      'technical_limit',
      'technical_lab_limit',
      'sales_limit',
      'pre_sales_limit',
    ],
    labels: {
      id:                         'ID',
      partner_one_id:             'Partner One ID',
      esi_partner_name:           'ESI Partner Name',
      partner_group:              'Partner Group',
      type:                       'Type',
      allowed_seats:              'Allowed Seats',
      technical_limit:            'Technical Limit',
      technical_lab_limit:        'Tech Lab Limit',
      sales_limit:                'Sales Limit',
      pre_sales_limit:            'Pre-Sales Limit',
      distributable_seats:        'Distributable Seats',
      is_block:                   'Blocked',
      is_jump_start:              'Jump Start',
      microsoft_account_manager:  'Microsoft AM',
      pdm_reporting_manager:      'PDM Manager',
      managed_by:                 'Managed By',
      created_at:                 'Created At',
      updated_at:                 'Updated At',
      partner_org_rm_user_id:     'Partner Org RM',
    },
    boolCols:  ['is_block', 'is_jump_start'],
    monoCols:  ['partner_one_id'],
    badgeCols: ['partner_group', 'type'],
  },
  'allowed-domains': {
    title:       'allowed_domains',
    heading:     'ESI Domains',
    subtitle:    'ESI / MPL / GSI partner domains (~10 700 rows). Source of truth for ESI registration.',
    breadcrumb:  'ESI Domains',
    labels: {
      id:             'ID',
      name:           'Domain',
      partner_one_id: 'Partner One ID',
      type:           'Type',
      allowed_seats:  'Allowed Seats',
      created_at:     'Created At',
      updated_at:     'Updated At',
    },
    boolCols:  [],
    monoCols:  ['name', 'partner_one_id'],
    badgeCols: ['type'],
  },
  'tiers': {
    title:       'partner_tiers',
    subtitle:    '~50 Top 50 CSP partners seeded from the programme CSV.',
    breadcrumb:  'Partner Tiers',
    labels: {
      id:             'ID',
      partner_one_id: 'Partner One ID',
      partner_name:   'Partner Name',
      maicpp_tier:    'MAICPP Tier',
      is_top50_csp:   'Top 50 CSP',
      is_active:      'Active',
      max_siab_users: 'Max SIAB Users',
      notes:          'Notes',
      source:         'Source',
      created_at:     'Created At',
      updated_at:     'Updated At',
    },
    boolCols:  ['is_top50_csp', 'is_active'],
    monoCols:  ['partner_one_id'],
    badgeCols: ['maicpp_tier', 'source'],
  },
  'tier-domains': {
    title:       'partner_tier_domains',
    subtitle:    '~620 domains for Top 50 CSP partners. Used by SIAB SSO gateway.',
    breadcrumb:  'Tier Domains',
    labels: {
      id:             'ID',
      partner_one_id: 'Partner One ID',
      domain:         'Domain',
      source:         'Source',
      is_active:      'Active',
      created_at:     'Created At',
      updated_at:     'Updated At',
    },
    boolCols:  ['is_active'],
    monoCols:  ['domain', 'partner_one_id'],
    badgeCols: ['source'],
  },
};

// ─── Shared badge colours for common values ────────────────────────────────

const BADGE_COLOR = {
  ESI_MPL_GSI:       'badge-green',
  CSP:               'badge-blue',
  CSP_DISTI:         'badge-blue',
  CSP_NON_DISTI:     'badge-teal',
  ManagedReseller:   'badge-purple',
  MANAGED_RESELLER:  'badge-purple',
  MaicppUnmanaged:   'badge-orange',
  MAICPP_UNMANAGED:  'badge-orange',
  Recruit:           'badge-yellow',
  RECRUIT:           'badge-yellow',
  INTERNAL:          'badge-gray',
  top50_csp:         'badge-blue',
  internal:          'badge-gray',
  manual:            'badge-purple',
  'ESI/MPL Partner Skilling': 'badge-green',
  'Non-ESI':         'badge-gray',
};

function Badge({ value }) {
  if (value === null || value === undefined || value === '') {
    return <span style={{ color: '#ccc' }}>—</span>;
  }
  const cls = BADGE_COLOR[value] || 'badge-gray';
  return (
    <span className={`badge ${cls}`} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
      {String(value)}
    </span>
  );
}

function BoolCell({ value }) {
  const yes = value === 1 || value === true || value === '1';
  if (yes) return <span className="badge badge-green"  style={{ fontSize: 11 }}>Yes</span>;
  return         <span className="badge badge-gray" style={{ fontSize: 11, color: '#999' }}>No</span>;
}

function CellValue({ col, value, meta }) {
  if (value === null || value === undefined) return <span style={{ color: '#ccc' }}>—</span>;
  if (meta.boolCols.includes(col))  return <BoolCell value={value} />;
  if (meta.badgeCols.includes(col)) return <Badge value={value} />;
  // Format ISO datetime strings
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    try {
      const d = new Date(value);
      return (
        <span style={{ fontSize: 11, color: '#718096', whiteSpace: 'nowrap' }}>
          {d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
      );
    } catch { /* fall through */ }
  }
  if (meta.monoCols.includes(col))  return (
    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{String(value)}</span>
  );
  return <span>{String(value)}</span>;
}

function Pagination({ page, totalPages, total, pageSize, onPage }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="pagination" data-testid="pt-pagination">
      <span className="pagination-info">
        {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

export default function PartnerTablePage() {
  const { tableKey } = useParams();
  const navigate     = useNavigate();
  const meta         = TABLE_META[tableKey];

  const [data,       setData]       = useState([]);
  const [columns,    setColumns]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [applied,    setApplied]    = useState('');
  const [exporting,  setExporting]  = useState(false);

  const fetchData = useCallback(async (p, q) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: 50 };
      if (q) params.search = q;
      const r = await axios.get(`${API}/api/partners/table/${tableKey}`,
        { params, withCredentials: true });
      setData(r.data.data);
      setColumns(r.data.columns);
      setTotal(r.data.total);
      setTotalPages(r.data.total_pages);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tableKey]); // eslint-disable-line

  useEffect(() => {
    setSearch('');
    setApplied('');
    fetchData(1, '');
  }, [tableKey]); // eslint-disable-line

  // Apply explicit column order if defined (must be before early return — Rules of Hooks)
  const orderedColumns = React.useMemo(() => {
    if (!meta?.columnOrder || columns.length === 0) return columns;
    const colSet = new Set(columns);
    const ordered = meta.columnOrder.filter(c => colSet.has(c));
    const rest    = columns.filter(c => !meta.columnOrder.includes(c));
    return [...ordered, ...rest];
  }, [columns, meta]); // eslint-disable-line

  if (!meta) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>
        Unknown table. <button onClick={() => navigate('/partners')} style={{ color: '#3182ce', background: 'none', border: 'none', cursor: 'pointer' }}>← Back to Partner Dashboard</button>
      </div>
    );
  }

  const handleSearch = () => { setApplied(search); fetchData(1, search); };
  const handleReset  = () => { setSearch(''); setApplied(''); fetchData(1, ''); };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch up to 10k rows for export
      const params = { page: 1, page_size: 200 };
      if (applied) params.search = applied;
      // Paginate through all pages
      const first = await axios.get(`${API}/api/partners/table/${tableKey}`,
        { params, withCredentials: true });
      const allRows = [...first.data.data];
      const tp = first.data.total_pages;
      for (let pg = 2; pg <= Math.min(tp, 50); pg++) {
        const r = await axios.get(`${API}/api/partners/table/${tableKey}`,
          { params: { ...params, page: pg }, withCredentials: true });
        allRows.push(...r.data.data);
      }
      const cols = first.data.columns;
      const exportCols = meta.columnOrder
        ? [...meta.columnOrder.filter(c => cols.includes(c)), ...cols.filter(c => !meta.columnOrder?.includes(c))]
        : cols;
      const csvRows = [
        exportCols.map(c => meta.labels[c] || c).join(','),
        ...allRows.map(row =>
          exportCols.map(c => {
            const v = row[c];
            if (v === null || v === undefined) return '';
            const s = String(v).replace(/"/g, '""');
            return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s}"` : s;
          }).join(',')
        ),
      ].join('\n');
      const blob = new Blob([csvRows], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `${tableKey}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setExporting(false); }
  };

  const colLabel = (col) => meta.labels[col] || col;

  return (
    <div data-testid={`partner-table-page-${tableKey}`}>
      {/* Breadcrumb */}
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        Dashboard &gt; <span
          style={{ cursor: 'pointer', color: '#3182ce' }}
          onClick={() => navigate('/partners')}
          data-testid="pt-back-link"
        >Partner Dashboard</span> &gt; {meta.breadcrumb}
      </div>

      {/* Header */}
      <div className="page-header">
        <h1 style={{ fontFamily: 'monospace', fontSize: 22 }}>{meta.heading || meta.title}</h1>
        <p style={{ color: '#666', fontSize: 13, margin: '4px 0 0' }}>{meta.subtitle}</p>
      </div>

      {/* Search bar */}
      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group" style={{ flex: 3 }}>
          <label className="filter-label">Search</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              className="filter-input"
              style={{ paddingLeft: 30 }}
              placeholder={`Search ${meta.title}…`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              data-testid="pt-search-input"
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary"   onClick={handleSearch} data-testid="pt-search-btn">Search</button>
          <button className="btn btn-secondary" onClick={handleReset}  data-testid="pt-reset-btn">Reset</button>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <div className="table-header">
          <div>
            <span className="table-title" style={{ fontFamily: 'monospace' }}>{meta.title}</span>
            <span className="table-count" style={{ marginLeft: 8 }}>({total.toLocaleString()} rows)</span>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#217346',
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
            data-testid="pt-export-btn"
          >
            <Download size={16} /> {exporting ? 'Exporting…' : 'CSV'}
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : data.length === 0 ? (
          <div className="empty-state">
            <h3>No rows found</h3>
            <p>{applied ? 'Try a different search term' : 'Table appears to be empty'}</p>
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table data-testid={`pt-table-${tableKey}`}>
                <thead>
                  <tr>
                    {orderedColumns.map(col => (
                      <th key={col} title={col}>{colLabel(col)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} data-testid={`pt-row-${i}`}>
                      {orderedColumns.map(col => (
                        <td key={col}>
                          <CellValue col={col} value={row[col]} meta={meta} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={50}
              onPage={p => fetchData(p, applied)} />
          </>
        )}
      </div>
    </div>
  );
}
